import React, { useEffect, useMemo, useState } from 'react'
import TabGroup from '@embracesbs/component-tabgroup/TabGroup'
import TenantDeploymentSettings from './TenantDeploymentSettings'
import AuditLog from './AuditLog'
import TenantRuntimeSettings from './Runtime/TenantRunTimeSettings'
import { useTenants } from '../Context/TenantContext'
import { useSubscriptions } from '../Context/SubscriptionContext'
import FeatureFlag from './Runtime/FeatureFlag'
import HealthCheck from './HealthCheck'
import SubscriptionDetails from '../Subscriptions/SubscriptionDetails'
import TenantAdvanced from './TenantAdvanced'
import EmptyState from '@embracesbs/component-emptystate'

interface TenantDetailsProps {}

const TenantDetails: React.FC<TenantDetailsProps> = ({}) => {
  const { selectedSubscriptionId } = useSubscriptions()
  const { selectedTenantId, selectedTenant } = useTenants()

  const [selectedTab, setSelectedTab] = useState('Deploytime Settings')

  const handleClick = (id: string) => {
    setSelectedTab(id)
  }

  useEffect(() => {
    setSelectedTab('Deploytime Settings')
  }, [selectedTenantId])

  const renderDetailsContent = useMemo(() => {
    switch (selectedTab) {
      case 'Deploytime Settings':
        return <TenantDeploymentSettings />
      case 'Runtime Settings':
        return <TenantRuntimeSettings />
      case 'Feature Flags':
        return <FeatureFlag />
      case 'Audit Log':
        return <AuditLog />
      case 'Health Checks':
        return <HealthCheck />
      case 'Advanced':
        return <TenantAdvanced />
      default:
        return <div>Not yet implemented</div>
    }
  }, [selectedTab])

  const tabs = useMemo(
    () => [
      {
        id: 'Deploytime Settings',
        text: 'Deploytime'
      },
      {
        id: 'Runtime Settings',
        text: 'Runtime'
      },
      {
        id: 'Feature Flags',
        text: 'Feature Flags'
      },
      {
        id: 'Audit Log',
        text: 'Audit Log'
      },
      {
        id: 'Health Checks',
        text: 'Health Checks'
      },
      {
        id: 'Advanced',
        text: 'Advanced'
      }
    ],
    []
  )

  if (!selectedSubscriptionId)
    return (
      <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <EmptyState
          imageName='customer360'
          size='medium'
          title='Nothing selected'
          text='Select a subscription from the list to see its tenants and details.'
        />
      </div>
    )
  else if (!selectedTenantId) {
    return <SubscriptionDetails />
  }

  if (selectedTenant?.deployStatus === 'initializing') {
    return (
      <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <EmptyState
          imageName='rocket'
          size='medium'
          title={`${selectedTenant.tenantName} is initializing`}
          text='Follow the progress via the pipeline link in the tenant properties.'
        />
      </div>
    )
  }

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          flexShrink: 0,
          height: '52px',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          marginLeft: '-40px',
          marginRight: '-40px'
        }}
      >
        <TabGroup
          onTabClick={handleClick}
          tabs={tabs}
          activeKey={selectedTab}
          canTabsGrow
          hasFullWidthBorder
          spacing={{ top: 0, bottom: 0 }}
        />
      </div>
      {/* Bleed to the panel edge so the scrollbar sits flush right; the padding gives the content its 40px back. */}
      <div
        style={{ flex: 1, minHeight: 0, overflowY: 'auto', marginTop: '12px', marginRight: '-40px', paddingRight: '40px' }}
        className='custom-scrollbar'
      >
        {renderDetailsContent}
      </div>
    </div>
  )
}

export default React.memo(TenantDetails)
